import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  ActivityIndicator,
  StatusBar,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

const API_BASE = 'https://api.jobeng.ai/api';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const SubscriptionStatusScreen = ({ navigation, route }) => {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [businessId, setBusinessId] = useState(null);

  useEffect(() => {
    loadBusinessId();
  }, []);

  const loadBusinessId = async () => {
    try {
      const companyData = await AsyncStorage.getItem('company_profile');
      if (companyData) {
        const company = JSON.parse(companyData);
        if (company && company.id) {
          setBusinessId(company.id);
          return;
        }
      }
      // Secondary: route.params (set by the screen that opened us) or a
      // generic company_id key. DO NOT fall back to a literal 1 — that
      // routes a logged-in user's data to the test tenant.
      if (route.params?.businessId) {
        setBusinessId(route.params.businessId);
        return;
      }
      const cid = await AsyncStorage.getItem('company_id');
      const parsed = cid ? parseInt(cid, 10) : null;
      setBusinessId(parsed && parsed > 0 ? parsed : null);
    } catch (error) {
      console.error('Error loading business ID:', error);
      setBusinessId(route.params?.businessId || null);
    }
  };

  useEffect(() => {
    if (businessId) {
      fetchSubscription();
    }
  }, [businessId]);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/business-subscriptions?company_id=${businessId}`, {
        headers: { 'X-API-Key': API_KEY },
      });
      const data = await response.json();
      if (data.success && data.data.length > 0) {
        setSubscription(data.data[0]);
      } else {
        setSubscription(null);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlanColor = () => {
    if (!subscription) return ['#0e4e96', '#1a6bb0'];

    switch (subscription.subscription_type) {
      case 'business': return ['#0e4e96', '#1a6bb0'];
      case 'professional': return ['#7C3AED', '#9D6CFA'];
      case 'learning': return ['#059669', '#10B981'];
      default: return ['#0e4e96', '#1a6bb0'];
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size={48} color="#0e4e96" />
        <Text style={styles.loadingText}>Loading subscription status...</Text>
      </View>
    );
  }

  if (!subscription) {
    return (
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="light-content" backgroundColor="#dc2626" />
        <LinearGradient colors={['#dc2626', '#b91c1c']} style={styles.header}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>No Active Subscription</Text>
          <View style={{ width: 40 }} />
        </LinearGradient>

        <View style={styles.noSubscriptionContainer}>
          <MaterialCommunityIcons name="cancel" size={80} color="#dc2626" />
          <Text style={styles.noSubscriptionTitle}>No Active Plan</Text>
          <Text style={styles.noSubscriptionText}>
            You don't have an active subscription. Choose a plan to get started!
          </Text>
          <TouchableOpacity
            style={styles.browseButton}
            onPress={() => navigation.navigate('PaymentScenariosScreen')}
          >
            <Text style={styles.browseButtonText}>View Plans</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const colors = getPlanColor();

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={colors[0]} />

      <LinearGradient colors={colors} style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Subscription Status</Text>
        <TouchableOpacity style={styles.refreshButton} onPress={fetchSubscription}>
          <Ionicons name="refresh" size={24} color="#fff" />
        </TouchableOpacity>
      </LinearGradient>

      <View style={styles.content}>
        <View style={styles.statusCard}>
          <View style={styles.statusHeader}>
            <View style={styles.iconContainer}>
              <MaterialCommunityIcons
                name={subscription.subscription_type === 'business' ? 'office-building' :
                      subscription.subscription_type === 'professional' ? 'account-tie' : 'school'}
                size={40}
                color={colors[0]}
              />
            </View>
            <View style={styles.statusInfo}>
              <Text style={styles.planType}>{subscription.plan_type} Plan</Text>
              <Text style={styles.planCategory}>{subscription.subscription_type}</Text>
            </View>
            <View style={[styles.statusBadge, { backgroundColor: subscription.status === 'active' ? '#10B981' : '#F59E0B' }]}>
              <Text style={styles.statusText}>{subscription.status}</Text>
            </View>
          </View>

          <View style={styles.detailsContainer}>
            <View style={styles.detailRow}>
              <View style={styles.detailIcon}>
                <Ionicons name="calendar" size={20} color="#64748b" />
              </View>
              <View style={styles.detailContent}>
                <Text style={styles.detailLabel}>Start Date</Text>
                <Text style={styles.detailValue}>{formatDate(subscription.start_date)}</Text>
              </View>
            </View>

            <View style={styles.detailRow}>
              <View style={styles.detailIcon}>
                <Ionicons name="calendar-outline" size={20} color="#64748b" />
              </View>
              <View style={styles.detailContent}>
                <Text style={styles.detailLabel}>End Date</Text>
                <Text style={styles.detailValue}>{formatDate(subscription.end_date)}</Text>
              </View>
            </View>

            <View style={styles.detailRow}>
              <View style={styles.detailIcon}>
                <Ionicons name="cash" size={20} color="#64748b" />
              </View>
              <View style={styles.detailContent}>
                <Text style={styles.detailLabel}>Price</Text>
                <Text style={styles.detailValue}>£{subscription.price_paid} / {subscription.billing_cycle}</Text>
              </View>
            </View>

            <View style={styles.detailRow}>
              <View style={styles.detailIcon}>
                <Ionicons name="people" size={20} color="#64748b" />
              </View>
              <View style={styles.detailContent}>
                <Text style={styles.detailLabel}>Users</Text>
                <Text style={styles.detailValue}>{subscription.users_count} users</Text>
              </View>
            </View>
          </View>

          {subscription.features && subscription.features.length > 0 && (
            <View style={styles.featuresContainer}>
              <Text style={styles.featuresTitle}>Features</Text>
              {subscription.features.map((feature, index) => (
                <View key={index} style={styles.featureRow}>
                  <Ionicons name="checkmark-circle" size={18} color="#10B981" />
                  <Text style={styles.featureText}>{feature}</Text>
                </View>
              ))}
            </View>
          )}
        </View>

        <TouchableOpacity
          style={styles.manageButton}
          onPress={() => navigation.navigate('BusinessPaymentSystem')}
        >
          <Text style={styles.manageButtonText}>Manage Subscription</Text>
          <Ionicons name="arrow-forward" size={20} color="#fff" />
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748b',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 20,
    borderBottomLeftRadius: 30,
    borderBottomRightRadius: 30,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#fff',
  },
  refreshButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  statusCard: {
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    marginBottom: 20,
  },
  statusHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  iconContainer: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#e6f0ff',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  statusInfo: {
    flex: 1,
  },
  planType: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 4,
  },
  planCategory: {
    fontSize: 14,
    color: '#64748b',
    textTransform: 'capitalize',
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
    textTransform: 'uppercase',
  },
  detailsContainer: {
    marginBottom: 20,
  },
  detailRow: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  detailIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f1f5f9',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  detailContent: {
    flex: 1,
    justifyContent: 'center',
  },
  detailLabel: {
    fontSize: 14,
    color: '#64748b',
    marginBottom: 2,
  },
  detailValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
  },
  featuresContainer: {
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 20,
  },
  featuresTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 12,
  },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  featureText: {
    fontSize: 14,
    color: '#334155',
    marginLeft: 10,
    flex: 1,
  },
  manageButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 16,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  manageButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  noSubscriptionContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  noSubscriptionTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0f172a',
    marginTop: 20,
    marginBottom: 8,
  },
  noSubscriptionText: {
    fontSize: 16,
    color: '#64748b',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 24,
  },
  browseButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderRadius: 12,
  },
  browseButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
});

export default SubscriptionStatusScreen;